import { createContext, useContext, useMemo } from 'react'
import { useMutation, useQueries, useQuery, useQueryClient } from 'react-query'
import cloneDeep from 'lodash.clonedeep'

import { useAuthentication } from '@hooks/useAuthentication'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { useContentLevel } from '@hooks/useContentLevel'
import { useChapterAction } from '@hooks/chapter/useChapterAction'
import { useUserAction } from '@hooks/user/useUserAction'
import { StoryDetailReaderType } from '@models/story/StoryDetailReaderType'
import { LikeSuccessStatusEnum } from '@models/book/LikeSuccessStatusEnum'
import { FollowSuccessStatusEnum } from '@interfaces/FollowSuccessStatusEnum'
import { ContentLevelEnum } from '@interfaces/ContentLevelEnum'
import { AgeVerifyEnum } from '@interfaces/AgeVerifyEnum'
import { addComma, getErrorMessage, rateRIds } from '@lib/utils'
import { StoryReaderContextType } from './interface'
import { useBookAction } from './useBookAction'

export interface StoryReaderProviderType {
  children: React.ReactNode
  bookId: number
  initialData?: StoryDetailReaderType
}

const StoryReaderContext = createContext<StoryReaderContextType>({
  bookId: 0,
  data: undefined,
  chapters: [],
  chapterGroups: [],
  totalPrice: 0,
  isLoading: false,
  isOwner: true,
  isError: false,
  isToggleFollowUserLoading: false,
  isToggleFollowPenNameLoading: false,
  isFollowedPenName: false,
  isFollowedUser: false,
  bookBuyAllPromotionDetail: undefined,
  bookReadPromotionDetail: undefined,
  canAction: false,
  isLodingToggleBookLike: false,
  continueToRead: undefined,
  bookPromotionSales: [],
  isBuyAllChapterFetching: false,
  handleLikeBook: async () => {},
  handleFollowUser: () => {},
  handleFollowPenName: () => {},
  errorMessage: '',
})

export function StoryReaderProvider({
  children,
  bookId,
  initialData,
}: StoryReaderProviderType) {
  const isIncrementView = true
  const { isAuthenticated, canFetchApi, user } = useAuthentication()
  const loginModal = useModal({ modal: 'login' })
  const queryClient = useQueryClient()
  const bookClient = useBookAction()
  const chapterClient = useChapterAction()
  const userClient = useUserAction()
  const { checkBookRate18Plus } = useContentLevel({ isIncrementView })

  const [
    {
      data: book,
      isLoading: isBookLoading,
      isError: isBookError,
      error: bookError,
    },
    { data: chapters = [], isFetching: isBuyAllChapterFetching },
    { data: continueToRead },
    { data: totalPrice = 0 },
  ] = useQueries([
    {
      queryKey: ['story-detail', bookId, 'reader'],
      queryFn: () =>
        bookClient.getStoryReader(bookId).then(res => {
          checkBookRate18Plus({
            bookId: res.id,
            isRateR: rateRIds.includes(res.rating.id),
            requireAgeVerify: res.requireAgeVerify,
            isOwner: res.penName.user.id === user.id,
            isAdmin: user.isAdmin,
            level: ContentLevelEnum.BOOK,
          })

          return res
        }),
      enabled: canFetchApi,
      initialData,
    },
    {
      queryKey: ['book', bookId, 'buy-all-book-chapter'],
      queryFn: () => chapterClient.getBuyAllBookChaptersReader(bookId),
      enabled: canFetchApi,
    },
    {
      queryKey: ['book', bookId, 'continue-to-read'],
      queryFn: () => chapterClient.getChapterForContinueToRead(bookId),
      enabled: canFetchApi,
    },
    {
      queryKey: ['total-price', bookId],
      queryFn: () => chapterClient.getAllPriceForBuyBook(bookId),
      enabled: canFetchApi,
    },
  ])

  const chapterGroups = useMemo(() => {
    const chapterCount = book ? book.publishedChapterCount : 0
    const chapterGroup = Math.ceil(chapterCount / 50)

    return Array.from({ length: chapterGroup }).map((_, index) => ({
      order: index,
      name: `${addComma(index * 50 + 1)} - ${addComma(
        Math.min(chapterCount, (index + 1) * 50)
      )}`,
    }))
  }, [book])

  const isOwner = useMemo(() => book?.penName.user.id === user.id, [book, user])
  const canAction =
    !!book &&
    ((book.requireAgeVerify && user.ageVerify === AgeVerifyEnum.APPROVED) ||
      !book.requireAgeVerify)

  const { data: isFollowedPenName = false } = useQuery(
    ['followed', 'pen-name', book?.penName.id],
    () => userClient.checkIsFollowedPenName(book!.penName.id),
    {
      enabled: !!book?.penName.id && isAuthenticated,
    }
  )

  const { data: isFollowedUser = false } = useQuery(
    ['followed', 'user', book?.penName.user.id],
    () => userClient.checkIsFollowedUser(book!.penName.user.id),
    {
      enabled: !!book?.penName.user.id && isAuthenticated,
    }
  )

  const { data: bookReadPromotionDetail } = useQuery(
    ['readFreePromotionDetail', bookId],
    () => chapterClient.getBookReadFreePromotionDetail(bookId),
    {
      enabled: !!bookId && canFetchApi,
    }
  )

  const { data: bookBuyAllPromotionDetail } = useQuery(
    ['buyAllPromotionDetail', bookId],
    () => chapterClient.getBookBuyAllPromotion(bookId),
    {
      enabled: !!bookId && canFetchApi,
    }
  )

  const { data: bookPromotionSales = [] } = useQuery(
    ['bookPromotionSales', bookId],
    () => chapterClient.getActivePromotionSales(bookId),
    {
      enabled: !!bookId,
    }
  )

  const { mutate: toggleFollowUser, isLoading: isToggleFollowUserLoading } =
    useMutation((userId: number) => userClient.toggleFollowUser(userId), {
      onSuccess: (type: FollowSuccessStatusEnum) => {
        queryClient.setQueryData<boolean>(
          ['followed', 'user', book?.penName.user.id],
          () => {
            return type === FollowSuccessStatusEnum.CREATE
          }
        )
      },
    })

  const {
    mutate: toggleFollowPenName,
    isLoading: isToggleFollowPenNameLoading,
  } = useMutation(
    (penNameId: number) => userClient.toggleFollowPenName(penNameId),
    {
      onSuccess: (type: FollowSuccessStatusEnum) => {
        queryClient.setQueryData<boolean>(
          ['followed', 'pen-name', book?.penName.id],
          () => {
            return type === FollowSuccessStatusEnum.CREATE
          }
        )
      },
    }
  )

  const { mutate: toggleBookLike, isLoading: isLodingToggleBookLike } =
    useMutation((id: number) => bookClient.toggleBookLike(id), {
      onSuccess: async type => {
        queryClient.setQueryData<StoryDetailReaderType | undefined>(
          ['story-detail', bookId, 'reader'],
          (oldData?: StoryDetailReaderType) => {
            if (oldData) {
              const temp = cloneDeep(oldData)
              temp.isLiked = type === LikeSuccessStatusEnum.CREATE
              temp.likeCount =
                type === LikeSuccessStatusEnum.CREATE
                  ? temp.likeCount + 1
                  : Math.max(0, temp.likeCount - 1)

              return temp
            }

            return oldData
          }
        )
        refetchBookData()
      },
    })

  async function handleLikeBook() {
    if (isAuthenticated) {
      await toggleBookLike(bookId)
    } else {
      loginModal.show()
    }
  }

  function handleFollowUser() {
    if (isAuthenticated && book) {
      toggleFollowUser(book.penName.user.id)
    } else {
      loginModal.show()
    }
  }

  function handleFollowPenName() {
    if (isAuthenticated && book) {
      toggleFollowPenName(book.penName.id)
    } else {
      loginModal.show()
    }
  }

  function refetchBookData() {
    // when user click like or view a book, need to fetch new data
    queryClient.refetchQueries('search_book', { active: true })
    queryClient.refetchQueries('get_newest', { active: true })
    queryClient.refetchQueries('latest_update', { active: true })
    queryClient.refetchQueries(['recommend_category'], { active: true })
    queryClient.refetchQueries('top-chart', { active: true })
    queryClient.refetchQueries('all-categories', { active: true })
    queryClient.refetchQueries('category', { active: true })
  }

  const value = {
    bookId,
    chapterGroups,
    chapters,
    data: book,
    totalPrice,
    isOwner,
    isLoading: isBookLoading,
    isError: isBookError,
    isToggleFollowUserLoading,
    isToggleFollowPenNameLoading,
    isFollowedPenName,
    isFollowedUser,
    bookBuyAllPromotionDetail,
    bookReadPromotionDetail,
    canAction,
    isLodingToggleBookLike,
    continueToRead,
    bookPromotionSales,
    isBuyAllChapterFetching,
    handleLikeBook,
    handleFollowUser,
    handleFollowPenName,
    errorMessage: bookError ? getErrorMessage(bookError) : '',
  }

  return (
    <StoryReaderContext.Provider value={value}>
      {children}
    </StoryReaderContext.Provider>
  )
}

export function useStoryReader() {
  return useContext(StoryReaderContext)
}
